import { fetchWithAuth } from "@/api/client";
import { attemptRefresh } from "@/auth/flow";
import { config } from "@/config";
import type { ImageSlot, MediaType } from "@/branding/images";

/**
 * Lab branding (Gate B white-labelling, contract section 4.9): `/api/v1/branding`.
 *
 * One BFF for the staff admin area and the client portal. **The permission decides reach,
 * never this client**: a client admin's request for an organisation outside their own
 * subtree is refused by the orchestrator on its organisation tree, whatever the page sends.
 * Every route exists only with SR_BRANDING_ENABLED on, which `/config.js` reports as
 * `brandingEnabled`.
 *
 * Its own module, and its own fetch: a refusal here carries a code and, for a value that
 * breaks a rule, a `[{field, code}]` list the editor puts next to each field. `apiFetch`
 * keeps only the sentence.
 */

/** One language's three emails. A missing message is `wording_incomplete` at save. */
export interface WordingMessage {
  subject: string;
  body: string[];
}
export interface WordingPinMessage extends WordingMessage {
  body_one_day?: string[];
}
export interface WordingSet {
  report_ready?: WordingMessage;
  report_updated?: WordingMessage;
  pin_reissued?: WordingPinMessage;
}

/** One organisation's own values (contract 4.2). Every key optional, absent means unset. */
export interface BrandingFields {
  display_name?: string;
  display_name_ar?: string;
  sender_name?: string;
  sender_name_ar?: string;
  logo_sha256?: string;
  primary_color?: string;
  ink_color?: string;
  contact_email?: string;
  contact_phone?: string;
  contact_address?: string;
  contact_website?: string;
  footer_line?: string;
  terms_url?: string;
  privacy_url?: string;
  stamp_sha256?: string;
  master_signature_holder_name?: string;
  master_signature_sha256?: string;
  show_approved_by?: boolean;
  show_approver_email?: boolean;
  show_prepared_by?: boolean;
  prepared_by?: string;
  timezone?: string;
  email_wording?: Record<string, WordingSet>;
}

export interface BrandingOrganization {
  organization_id: string;
  parent_id: string | null;
  name: string | null;
  has_branding: boolean;
  current_version: number | null;
}

/** One inheritance unit as it resolves, and which organisation and version supplied it. */
export interface ResolvedUnit {
  value: unknown;
  source_organization_id: string | null;
  source_version: number | null;
}

export type DomainStatus = "pending" | "verified" | "failed";

/**
 * One DNS record the lab adds, as the server lists it (plan B9). `found` is null on a read
 * and true or false only on the answer to a check, which looks each record up.
 */
export interface DomainDnsRecord {
  purpose: string;
  type: string;
  name: string;
  value: string;
  required: boolean;
  found: boolean | null;
}

export interface SendingDomain {
  organization_id: string;
  kind: "sending";
  domain: string;
  status: DomainStatus;
  failure_reason: string | null;
  dkim_records: { name: string; type: string; value: string }[];
  mail_from_domain: string | null;
  /** Every record to add, MAIL FROM included. Absent from an older orchestrator. */
  records?: DomainDnsRecord[];
  checked_at: string | null;
  verified_at: string | null;
  updated_at: string;
}

export interface LinkDomain {
  organization_id: string;
  kind: "link";
  host: string;
  status: DomainStatus;
  failure_reason: string | null;
  cname_target: string;
  /** Every record to add. Absent from an older orchestrator. */
  records?: DomainDnsRecord[];
  checked_at: string | null;
  verified_at: string | null;
  updated_at: string;
}

/** What the read says about one image, by hash. Never its bytes. */
export interface BrandingImageInfo {
  media_type: MediaType;
  byte_size: number;
  width_px: number;
  height_px: number;
}

export interface BrandingRecord {
  own: BrandingFields;
  /** Null until the organisation's first save. */
  version: number | null;
  resolved: Record<string, ResolvedUnit>;
  ink: string | null;
  ink_derived: boolean;
  /** Every image the resolved branding would carry. Absent from an older orchestrator. */
  images?: Record<string, BrandingImageInfo>;
  sending_domain: SendingDomain | null;
  link_domain: LinkDomain | null;
}

export interface UploadedImage {
  sha256: string;
  media_type: MediaType;
  byte_size: number;
  width_px: number;
  height_px: number;
}

export interface BrandingVersion {
  version: number;
  actor_sub: string;
  actor_kind: string;
  changed_fields: string[];
  created_at: string;
}

/** The portal's look for the signed-in user's organisation (D4). */
export type PortalTheme =
  | { medicus: true }
  | {
      display_name: string | null;
      logo: { media_type: MediaType; data_uri: string } | null;
      primary: string | null;
      ink: string | null;
    };

export interface SignatureAnswer {
  sha256: string | null;
  version: number | null;
}

export interface BrandingFieldError {
  field: string;
  code: string;
}

/** A refusal from `/api/v1/branding`: its code, its sentence and any field codes. */
export class BrandingApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    message: string,
    readonly fieldErrors: BrandingFieldError[] = [],
  ) {
    super(message);
    this.name = "BrandingApiError";
  }
}

const BASE = "/api/v1/branding";

function segment(value: string): string {
  return encodeURIComponent(value);
}

async function send(path: string, init: RequestInit = {}): Promise<Response> {
  const url = `${config.apiBaseUrl}${BASE}${path}`;
  let resp = await fetchWithAuth(url, init);
  if (resp.status === 401 && (await attemptRefresh())) {
    resp = await fetchWithAuth(url, init);
  }
  if (!resp.ok) {
    throw await refusal(resp);
  }
  return resp;
}

async function refusal(resp: Response): Promise<BrandingApiError> {
  const fallback = `API request failed: ${resp.status} ${resp.statusText}`;
  try {
    const body = (await resp.json()) as {
      error?: { code?: unknown; detail?: unknown; message?: unknown; details?: unknown };
    };
    const error = body.error ?? {};
    const code = typeof error.code === "string" ? error.code : null;
    const sentence =
      typeof error.detail === "string" && error.detail.trim() !== ""
        ? error.detail
        : typeof error.message === "string" && error.message.trim() !== ""
          ? error.message
          : fallback;
    const details = error.details as { errors?: unknown } | undefined;
    const fieldErrors = Array.isArray(details?.errors)
      ? details.errors.filter(
          (e): e is BrandingFieldError =>
            typeof e === "object" &&
            e !== null &&
            typeof (e as BrandingFieldError).field === "string" &&
            typeof (e as BrandingFieldError).code === "string",
        )
      : [];
    return new BrandingApiError(resp.status, code, sentence, fieldErrors);
  } catch {
    return new BrandingApiError(resp.status, null, fallback);
  }
}

async function json<T>(path: string, init: RequestInit = {}): Promise<T> {
  return (await (await send(path, init)).json()) as T;
}

/** The caller's organisation and every one under it, with their branding versions. */
export function listBrandingOrganizations(): Promise<BrandingOrganization[]> {
  return json<BrandingOrganization[]>("/organizations");
}

export function getBranding(organizationId: string): Promise<BrandingRecord> {
  return json<BrandingRecord>(`/${segment(organizationId)}`);
}

/** Publish a new version, the full own set. 409 when `expectedVersion` is not current. */
export function putBranding(
  organizationId: string,
  expectedVersion: number | null,
  fields: BrandingFields,
): Promise<{ version: number }> {
  return json<{ version: number }>(`/${segment(organizationId)}`, {
    method: "PUT",
    body: JSON.stringify({ expected_version: expectedVersion, fields }),
  });
}

/** Store one image for a slot. It takes effect when a save names its hash. */
export function uploadBrandingImage(
  organizationId: string,
  slot: Exclude<ImageSlot, "signature">,
  file: Blob,
): Promise<UploadedImage> {
  const form = new FormData();
  form.append("file", file);
  form.append("slot", slot);
  return json<UploadedImage>(`/${segment(organizationId)}/images`, { method: "POST", body: form });
}

export function listBrandingVersions(organizationId: string): Promise<BrandingVersion[]> {
  return json<BrandingVersion[]>(`/${segment(organizationId)}/versions`);
}

export function putSendingDomain(organizationId: string, domain: string): Promise<SendingDomain> {
  return json<SendingDomain>(`/${segment(organizationId)}/domains/sending`, {
    method: "PUT",
    body: JSON.stringify({ domain }),
  });
}

export function putLinkDomain(organizationId: string, host: string): Promise<LinkDomain> {
  return json<LinkDomain>(`/${segment(organizationId)}/domains/link`, {
    method: "PUT",
    body: JSON.stringify({ host }),
  });
}

export async function deleteDomain(
  organizationId: string,
  kind: "sending" | "link",
): Promise<void> {
  await send(`/${segment(organizationId)}/domains/${kind}`, { method: "DELETE" });
}

/**
 * Check the domain now against SES and DNS: the row after the check, with `found` on each
 * record. No body. A 503 `branding_unavailable` means SES or DNS gave no answer and nothing
 * changed.
 */
export function checkBrandingDomain(
  organizationId: string,
  kind: "sending" | "link",
): Promise<SendingDomain | LinkDomain> {
  return json<SendingDomain | LinkDomain>(`/${segment(organizationId)}/domains/${kind}/check`, {
    method: "POST",
  });
}

function previewBody(language: "en" | "ar", fields?: BrandingFields): string {
  return JSON.stringify(fields === undefined ? { language } : { language, fields });
}

/**
 * report-summary's synthetic report page in the lab's branding, from a draft or, with no
 * `fields`, the saved record. **HTML carrying lab-controlled text: show it only in a
 * sandboxed iframe.**
 */
export async function previewBrandingPage(
  organizationId: string,
  language: "en" | "ar",
  fields?: BrandingFields,
): Promise<string> {
  const resp = await send(`/${segment(organizationId)}/preview/page`, {
    method: "POST",
    body: previewBody(language, fields),
  });
  return resp.text();
}

/** The sample PDF in the lab's branding, as a Blob typed `application/pdf` whatever was sent. */
export async function previewBrandingPdf(
  organizationId: string,
  language: "en" | "ar",
  fields?: BrandingFields,
): Promise<Blob> {
  const resp = await send(`/${segment(organizationId)}/preview/pdf`, {
    method: "POST",
    body: previewBody(language, fields),
  });
  return new Blob([await resp.arrayBuffer()], { type: "application/pdf" });
}

/**
 * The signed-in user's own organisation's SAVED patient page, read only (plan B7), for the
 * lab doctor on the report review page. The organisation is the token's, so nothing here
 * names one. **HTML carrying lab-controlled text: show it only in a sandboxed iframe.**
 */
export async function previewReviewPage(language: "en" | "ar"): Promise<string> {
  const resp = await send(`/review-preview/page?language=${segment(language)}`);
  return resp.text();
}

export type EmailPreviewMessage = "report_ready" | "report_updated" | "pin_reissued";

/** The patient email as the send renders it (contract 4.9). `html` carries lab text. */
export interface EmailPreview {
  subject: string;
  text: string;
  html: string;
  from_display: string;
}

/**
 * The patient email the lab's branding would produce, rendered by Smart Reports with the
 * code that sends it, from a draft or, with no `fields`, the saved record. A sample link
 * and PIN. **`html` carries lab-controlled text: show it only in a sandboxed iframe.**
 */
export function previewBrandingEmail(
  organizationId: string,
  language: string,
  message: EmailPreviewMessage,
  oneDay: boolean,
  fields?: BrandingFields,
): Promise<EmailPreview> {
  const body: Record<string, unknown> = { language, message, one_day: oneDay };
  if (fields !== undefined) body.fields = fields;
  return json<EmailPreview>(`/${segment(organizationId)}/preview/email`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** The look of the portal for the signed-in user, `{medicus: true}` for Medicus staff. */
export function getPortalTheme(): Promise<PortalTheme> {
  return json<PortalTheme>("/theme");
}

export function getOwnSignature(): Promise<SignatureAnswer> {
  return json<SignatureAnswer>("/signatures/me");
}

export function putOwnSignature(file: Blob): Promise<SignatureAnswer> {
  const form = new FormData();
  form.append("file", file);
  return json<SignatureAnswer>("/signatures/me", { method: "PUT", body: form });
}

export function deleteOwnSignature(): Promise<SignatureAnswer> {
  return json<SignatureAnswer>("/signatures/me", { method: "DELETE" });
}

/** A doctor's signature set by an admin within reach. Filed under `organizationId` if new. */
export function putDoctorSignature(
  iamUserId: string,
  file: Blob,
  organizationId?: string,
): Promise<SignatureAnswer> {
  const form = new FormData();
  form.append("file", file);
  if (organizationId !== undefined) form.append("organization_id", organizationId);
  return json<SignatureAnswer>(`/signatures/${segment(iamUserId)}`, { method: "PUT", body: form });
}

export function deleteDoctorSignature(iamUserId: string): Promise<SignatureAnswer> {
  return json<SignatureAnswer>(`/signatures/${segment(iamUserId)}`, { method: "DELETE" });
}
